import { useState, useEffect } from "react";
import axios from "../../config/axiosInstance";
import PageHeader from "../../component/pageHeaders";
import { useNavigate, useLocation } from "react-router-dom";
import { API } from "../../config/API";
import "./styles.css";
import { Button, message, Select, Spin } from "antd";
import { HiRefresh } from "react-icons/hi";
import NewForm from "./component/newform";
import DataTable from "./component/datatable";
import SearchInput from "../../component/SearchInput";
import { DELETE } from "../../utils/apirequest";

const { Option } = Select;

function ReelsScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const query: any = new URLSearchParams(location.search);
  const [isReels, setIsReels] = useState(true);
  const [data, setData] = useState<any[]>([]);
  const [meta, setMeta] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(query.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(query.get("status") || "");
  const [page, setPage] = useState(
    query.get("page") ? parseInt(query.get("page")) : 1
  );
  const [pageSize, setPageSize] = useState(
    query.get("limit") ? parseInt(query.get("limit")) : 10
  );

  useEffect(() => {
    const currentQuery = new URLSearchParams(location.search);
    const currentPage = currentQuery.get("page") ? parseInt(currentQuery.get("page")!) : 1;
    const currentPageSize = currentQuery.get("limit") ? parseInt(currentQuery.get("limit")!) : 10;
    const currentSearch = currentQuery.get("search") || "";
    const currentStatus = currentQuery.get("status") || "";
    setPage(currentPage);
    setPageSize(currentPageSize);
    setSearchTerm(currentSearch);
    setStatusFilter(currentStatus);
    loadDataWithParams(currentPage, currentPageSize, currentSearch, currentStatus);
  }, [location.search]);

  // Clear statusFilter if it's set to "trash" (edge case handling)
  useEffect(() => {
    if (statusFilter === "trash") {
      setStatusFilter("");
      const currentQuery = new URLSearchParams(location.search);
      currentQuery.delete("status");
      navigate({ search: currentQuery.toString() });
    }
  }, [statusFilter, location.search, navigate]);


  const transformData = (backendData: any[]) => {
    return backendData.map((item: any) => {
      return {
        id: item.id,
        video: item.video || null,
        title: item.title || null,
        head: item.head || null,
        description: item.description || null,
        createdAt: item.createdAt || null,
        publishedAt: item.publishedAt || null,
        is_deleted: item.is_deleted || false,
        tags: item.tags || [],
        user: item.user
          ? {
              id: item.user.id,
              username: item.user.username || "N/A",
            }
          : null,
      };
    });
  };

  const loadDataWithParams = (currentPage: number, currentPageSize: number, searchTerm: string = "", statusFilter: string = "") => {
    setIsLoading(true);
    // Build query string with pagination and filters
    let filters = `?page=${currentPage}&pageSize=${currentPageSize}`;
    if (searchTerm) {
      filters += `&search=${encodeURIComponent(searchTerm)}`;
    }
    if (statusFilter) {
      filters += `&status=${encodeURIComponent(statusFilter)}`;
    }
    let url = API.BASE_URL + API.REELS + filters;
    axios
      .get(url)
      .then((response) => {
        let backendData = response.data.data || [];
        // Transform backend data to flat structure
        let transformedData = transformData(backendData);
        setData(transformedData);
        // Extract total count from server response
        const total = response.data.meta?.pagination?.total || 0;
        setMeta(total);
        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
        message.error("Failed to load reels");
      });
  };

  const loadData = () => {
    loadDataWithParams(page, pageSize, searchTerm, statusFilter);
  };

  const onPageChange = (page: any, pageSize: any) => {
    const currentQuery = new URLSearchParams(location.search);
    currentQuery.set("page", page.toString());
    currentQuery.set("limit", pageSize.toString());
    navigate({ search: currentQuery.toString() });
    window.scrollTo(0, 0);
  };

  const handleDelete = async (record: any) => {
    let url = `reels/${record.id}`;
    const res: any = await DELETE(url);
    if (res?.data?.id) {
      message.success("Item Deleted");
      
      // Calculate remaining items after deletion
      const remainingItems = data.filter((item) => item.id !== record.id);
      
      // Optimistically remove the deleted item from local state
      setData(remainingItems);
      
      // Decrement total count
      setMeta((prev) => Math.max(0, prev - 1));
      
      // Handle edge case: If current page becomes empty and we're not on page 1, navigate to previous page
      if (remainingItems.length === 0 && page > 1) {
        const previousPage = page - 1;
        const currentQuery = new URLSearchParams(location.search);
        currentQuery.set("page", previousPage.toString());
        navigate({ search: currentQuery.toString() });
        // useEffect will trigger reload with new page
      }
      // If page 1 becomes empty, just show empty state (no reload needed)
    } else {
      message.error("Something went wrong");
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    // Reset to first page when searching
    const currentQuery = new URLSearchParams(location.search);
    currentQuery.set("page", "1");
    if (value) {
      currentQuery.set("search", value);
    } else {
      currentQuery.delete("search");
    }
    navigate({ search: currentQuery.toString() });
  };

  const handleStatusFilter = (value: string) => {
    const filterValue = value === "all" ? "" : value;
    setStatusFilter(filterValue);
    // Reset to first page when filtering
    const currentQuery = new URLSearchParams(location.search);
    currentQuery.set("page", "1");
    if (filterValue) {
      currentQuery.set("status", filterValue);
    } else {
      currentQuery.delete("status");
    }
    navigate({ search: currentQuery.toString() });
  };

  const handleTogglePublish = async (
    record: any,
    checked: boolean,
    onComplete: () => void
  ) => {
    try {
      const url = API.BASE_URL + API.REELS + `/${record.id}`;
      const requestObj = {
        data: {
          publishedAt: checked ? new Date() : null,
        },
      };

      const response = await axios.put(url, requestObj);
      
      if (response.data?.data) {
        message.success(
          checked ? "Reel published successfully" : "Reel unpublished successfully"
        );
        // Reload current page from server to get updated data
        loadData();
        onComplete();
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error: any) {
      console.error("Error toggling publish status:", error);
      message.error(
        error?.response?.data?.error?.message ||
        "Failed to update publish status. Please try again."
      );
      // Reload data on error
      loadData();
      onComplete(); // Clear loading state even on error
    }
  };

  return (
    <>
      <div>
        {isReels && (
          <PageHeader
            title={"Reels"}
            length={`${meta || 0} Reels`}
            loader={false}
            onBack={() => navigate("/Auth/")}
          >
            <div>
              <Button
                type="primary"
                ghost
                icon={<HiRefresh />}
                onClick={() => loadData()}
              >
                Refresh
              </Button>
            </div>
            <div
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <SearchInput
                onSearch={handleSearch}
                placeholder="Search reels..."
              />

              <Select
                placeholder="Filter by status"
                style={{ width: 150 }}
                value={statusFilter || null}
                onChange={handleStatusFilter}
                allowClear
              >
                <Option value="published">Published</Option>
                <Option value="draft">Draft</Option>
              </Select>

              <Button type="primary" onClick={() => setIsReels(false)}>
                Add New +
              </Button>
            </div>
          </PageHeader>
        )}

        {isReels ? (
          isLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
              <div style={{ padding: 4 }} />
              <DataTable
                data={data}
                meta={meta}
                page={page}
                pagesize={pageSize}
                loading={isLoading}
                refresh={() => loadData()}
                onDelete={(val: any) => handleDelete(val)}
                onTogglePublish={handleTogglePublish}
                onPageChange={(page: any, pageSize: any) =>
                  onPageChange(page, pageSize)
                }
              />
            </>
          )
        ) : (
          <NewForm onClose={() => { setIsReels(true); loadData(); }} reload={() => loadData()} />
        )}
      </div>
    </>
  );
}

export default ReelsScreen;
